/**
 * The leaderboards (Phase 5, BUILD_PLAN section 7): all-time score, this week's score (from
 * Monday 00:00 UTC), ranked rating (placed pilots only) and score per mech. The API refreshes
 * the boards every thirty seconds; the page re-asks every ten and says how fresh they are.
 */
import { MECHS } from '@steelwake/shared/data';
import {
  LeaderboardSchema,
  type BoardName,
  type Leaderboard as BoardView,
  type LeaderboardRow,
} from '@steelwake/shared/progression';
import { Button, colors, fonts, spacing } from '@steelwake/ui';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { api, describeError } from '../api.js';

const TABS: { id: BoardName; label: string; column: string }[] = [
  { id: 'alltime', label: 'All-time', column: 'Score' },
  { id: 'weekly', label: 'This week', column: 'Score' },
  { id: 'rating', label: 'Ranked', column: 'Rating' },
  { id: 'mech', label: 'By mech', column: 'Score' },
];
const REFETCH_MS = 10_000;

function isBoard(value: string | null): value is BoardName {
  return TABS.some((t) => t.id === value);
}

function freshness(refreshedAt: string | null, now: number): string {
  if (refreshedAt === null) return 'not refreshed yet';
  const seconds = Math.max(0, Math.round((now - Date.parse(refreshedAt)) / 1000));
  return `refreshed ${String(seconds)} s ago`;
}

const hasWins = (board: BoardName) => board === 'alltime' || board === 'weekly';

function Row({
  row,
  board,
  own = false,
}: {
  row: LeaderboardRow;
  board: BoardName;
  own?: boolean;
}) {
  const value =
    board === 'rating' ? String(Math.round(row.value)) : row.value.toLocaleString('en-US');
  return (
    <tr
      data-testid={own ? 'board-own' : `board-row-${String(row.rank)}`}
      data-callsign={row.callsign}
      style={{
        borderTop: `1px solid ${colors.border}`,
        background: row.mine ? colors.surfaceRaised : 'transparent',
        fontWeight: row.mine ? 700 : 400,
      }}
    >
      <td style={{ padding: '6px 4px' }}>{String(row.rank)}</td>
      <td>
        {row.callsign}
        {row.mine ? ' (you)' : ''}
      </td>
      <td style={{ textAlign: 'right' }}>
        {value}
        {board === 'rating' && row.rd !== null ? (
          <span style={{ color: colors.textMuted }}> ±{String(Math.round(row.rd))}</span>
        ) : null}
      </td>
      <td style={{ textAlign: 'right' }}>{String(row.matches)}</td>
      {hasWins(board) ? <td style={{ textAlign: 'right' }}>{String(row.wins ?? 0)}</td> : null}
    </tr>
  );
}

export function Leaderboard() {
  const [params, setParams] = useSearchParams();
  const boardParam = params.get('board');
  const board: BoardName = isBoard(boardParam) ? boardParam : 'alltime';
  const mech = params.get('mech');
  const [view, setView] = useState<BoardView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const query = board === 'mech' && mech ? `?mech=${encodeURIComponent(mech)}` : '';
        const next = LeaderboardSchema.parse(
          await api<unknown>(`/api/v1/leaderboard/${board}${query}`),
        );
        if (cancelled) return;
        setView(next);
        setError(null);
        setNow(Date.now());
      } catch (err) {
        if (!cancelled) setError(describeError(err));
      }
    };
    void load();
    const timer = setInterval(() => void load(), REFETCH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [board, mech]);

  // The "n s ago" line ticks between fetches.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const tab = TABS.find((t) => t.id === board) ?? TABS[0]!;
  const select = (next: BoardName, nextMech?: string) => {
    const p = new URLSearchParams();
    p.set('board', next);
    if (next === 'mech' && nextMech) p.set('mech', nextMech);
    setParams(p);
  };
  const shown = view && view.board === board ? view : null;
  const ownBelow = shown?.own && !shown.rows.some((r) => r.mine) ? shown.own : null;

  return (
    <div
      data-testid="leaderboard"
      style={{ width: 'min(760px, 96vw)', fontFamily: fonts.ui, color: colors.text }}
    >
      <h1 style={{ margin: `0 0 ${spacing.md}`, fontSize: 22, letterSpacing: '0.12em' }}>
        Leaderboards
      </h1>
      <div role="tablist" style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.md }}>
        {TABS.map((t) => (
          <Button
            key={t.id}
            role="tab"
            aria-selected={t.id === board}
            variant={t.id === board ? 'primary' : 'ghost'}
            data-testid={`board-tab-${t.id}`}
            onClick={() => select(t.id, shown?.mechId ?? undefined)}
          >
            {t.label}
          </Button>
        ))}
      </div>
      {board === 'mech' ? (
        <label style={{ display: 'block', fontSize: 13, marginBottom: spacing.md }}>
          <span style={{ color: colors.textMuted }}>Mech</span>
          <select
            data-testid="board-mech"
            value={shown?.mechId ?? ''}
            onChange={(e) => select('mech', e.target.value)}
            style={{ display: 'block', marginTop: 4 }}
          >
            {(shown?.mechs ?? []).map((m) => (
              <option key={m.mechId} value={m.mechId}>
                {MECHS[m.mechId]?.name ?? m.mechId} ({String(m.players)})
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <p data-testid="board-refreshed" style={{ color: colors.textMuted, fontSize: 12 }}>
        {shown ? freshness(shown.refreshedAt, now) : 'loading…'}
        {board === 'weekly'
          ? ' · resets Monday 00:00 UTC'
          : board === 'rating'
            ? ' · placed pilots only'
            : ''}
      </p>
      {error ? (
        <p role="alert" data-testid="board-error" style={{ color: colors.danger }}>
          {error}
        </p>
      ) : null}
      <div data-testid="board-scroll" style={{ overflowX: 'auto' }}>
        <table
          data-testid="board-table"
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}
        >
          <thead>
            <tr style={{ color: colors.textMuted, textAlign: 'left' }}>
              <th style={{ padding: '6px 4px', width: 60 }}>#</th>
              <th>Pilot</th>
              <th style={{ textAlign: 'right' }}>{tab.column}</th>
              <th style={{ textAlign: 'right' }}>Matches</th>
              {hasWins(board) ? <th style={{ textAlign: 'right' }}>Wins</th> : null}
            </tr>
          </thead>
          <tbody>
            {(shown?.rows ?? []).map((row) => (
              <Row key={row.rank} row={row} board={board} />
            ))}
            {ownBelow ? <Row row={ownBelow} board={board} own /> : null}
            {shown && shown.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  data-testid="board-empty"
                  style={{ padding: spacing.md, color: colors.textMuted }}
                >
                  Nobody on this board yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
